import { doc, getDoc, updateDoc, addDoc, collection, query, where, getDocs, Timestamp, writeBatch } from 'firebase/firestore';
import { db } from './firebase';
import { UserProfile, CampaignCode } from '@/types';
import { isCampaignValid } from './campaigns';

/**
 * ユーザーの使用済みコードを配列にマイグレーション
 */
const migrateUserCodes = (user: UserProfile): { inviteCodes: string[]; campaignCodes: string[] } => {
  const inviteCodes = [...(user.usedInviteCodes || [])];
  const campaignCodes = [...(user.usedCampaignCodes || [])];
  
  // 旧フィールドから移行
  if (user.usedInviteCode && !inviteCodes.includes(user.usedInviteCode)) {
    inviteCodes.push(user.usedInviteCode);
  }
  if (user.usedCampaignCode && !campaignCodes.includes(user.usedCampaignCode)) {
    campaignCodes.push(user.usedCampaignCode);
  }
  
  return { inviteCodes, campaignCodes };
};

/**
 * 招待コードを適用（被招待者が使用）
 */
export const applyInviteCode = async (userId: string, inviteCode: string): Promise<{ success: boolean; message: string }> => {
  try {
    // ユーザー情報を取得
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      return { success: false, message: 'ユーザーが見つかりません' };
    }
    
    const user = { uid: userSnap.id, ...userSnap.data() } as UserProfile;
    
    // 既に同じ招待コードを使用済みか確認（配列と単一フィールドの両方をチェック）
    const { inviteCodes: usedInviteCodes } = migrateUserCodes(user);
    if (usedInviteCodes.includes(inviteCode)) {
      return { success: false, message: 'この招待コードは既に使用済みです' };
    }
    
    // 招待コードを検索（usersコレクションのinvitationCodeフィールド）
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('invitationCode', '==', inviteCode));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return { success: false, message: '招待コードが見つかりません' };
    }
    
    const inviterDoc = querySnapshot.docs[0];
    const inviter = { uid: inviterDoc.id, ...inviterDoc.data() } as UserProfile;
    
    // 自分のコードは使用不可
    if (inviter.uid === userId) {
      return { success: false, message: '自分の招待コードは使用できません' };
    }
    
    const now = Timestamp.now();
    
    // 被招待者に30日間のStandardプラン付与
    const inviteeEndDate = new Date();
    inviteeEndDate.setDate(inviteeEndDate.getDate() + 30);
    
    // 被招待者の情報を更新
    const updatedInviteCodes = [...usedInviteCodes, inviteCode];
    await updateDoc(userRef, {
      plan: 'Standard',
      planStartDate: now,
      planEndDate: Timestamp.fromDate(inviteeEndDate),
      usedInviteCode: inviteCode, // 後方互換性のため保持
      usedInviteCodes: updatedInviteCodes, // 配列で管理
      updatedAt: now,
    });
    
    // 被招待者の利用履歴を記録
    const inviteeHistoryRef = collection(db, 'users', userId, 'codeUsageHistory');
    const inviteeHistoryData: Record<string, any> = {
      userId: user.uid,
      userName: user.displayName,
      userEmail: user.email,
      codeType: 'invite',
      code: inviteCode,
      appliedAt: now,
      planBefore: user.plan,
      planAfter: 'Standard',
      endDateAfter: Timestamp.fromDate(inviteeEndDate),
      rewardDays: 30,
      inviterId: inviter.uid,
    };
    // Only add endDateBefore if it exists
    if (user.planEndDate) {
      inviteeHistoryData.endDateBefore = user.planEndDate;
    }
    await addDoc(inviteeHistoryRef, inviteeHistoryData);
    
    // 招待者への報酬は「招待報酬ペンディング」コレクションに記録
    // （Cloud Functionsまたは管理画面から手動で付与）
    const pendingRewardsRef = collection(db, 'pendingInviteRewards');
    await addDoc(pendingRewardsRef, {
      inviterId: inviter.uid,
      inviterName: inviter.displayName,
      inviterEmail: inviter.email,
      inviteeId: user.uid,
      inviteeName: user.displayName,
      inviteeEmail: user.email,
      inviteCode: inviteCode,
      rewardDays: 60,
      status: 'pending',
      createdAt: now,
    });
    
    return { success: true, message: 'Standardプラン（30日間）を適用しました！' };
  } catch (error) {
    console.error('Error applying invite code:', error);
    return { success: false, message: '招待コードの適用に失敗しました' };
  }
};

/**
 * キャンペーンコードを適用
 */
export const applyCampaignCode = async (userId: string, campaignCode: string): Promise<{ success: boolean; message: string }> => {
  try {
    // ユーザー情報を取得
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      return { success: false, message: 'ユーザーが見つかりません' };
    }
    
    const user = { uid: userSnap.id, ...userSnap.data() } as UserProfile;
    
    // 既に同じキャンペーンコードを使用済みか確認（配列と単一フィールドの両方をチェック）
    const { campaignCodes: usedCampaignCodes } = migrateUserCodes(user);
    if (usedCampaignCodes.includes(campaignCode.toUpperCase())) {
      return { success: false, message: 'このキャンペーンコードは既に使用済みです' };
    }
    
    // キャンペーンコードを検索
    const campaignsRef = collection(db, 'campaignCodes');
    const q = query(campaignsRef, where('code', '==', campaignCode.toUpperCase()));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return { success: false, message: 'キャンペーンコードが見つかりません' };
    }
    
    const campaignDoc = querySnapshot.docs[0];
    const campaign = { id: campaignDoc.id, ...campaignDoc.data() } as CampaignCode;
    
    // キャンペーンの有効性をチェック
    if (!isCampaignValid(campaign)) {
      return { success: false, message: 'このキャンペーンコードは無効です' };
    }
    
    const now = Timestamp.now();
    const batch = writeBatch(db);
    
    // キャンペーンタイプに応じてプランを適用
    let endDate: Date | null = null;
    let message = '';
    
    switch (campaign.campaignType) {
      case 'free_1month':
        // 既存のプラン終了日がある場合は延長、ない場合は今日から
        if (user.planEndDate && user.planEndDate.toDate() > new Date()) {
          endDate = user.planEndDate.toDate();
          endDate.setMonth(endDate.getMonth() + 1);
        } else {
          endDate = new Date();
          endDate.setMonth(endDate.getMonth() + 1);
        }
        message = 'Standardプラン（1ヶ月無料）を適用しました！';
        break;
      case 'free_3months':
        if (user.planEndDate && user.planEndDate.toDate() > new Date()) {
          endDate = user.planEndDate.toDate();
          endDate.setMonth(endDate.getMonth() + 3);
        } else {
          endDate = new Date();
          endDate.setMonth(endDate.getMonth() + 3);
        }
        message = 'Standardプラン（3ヶ月無料）を適用しました！';
        break;
      case 'free_6months':
        if (user.planEndDate && user.planEndDate.toDate() > new Date()) {
          endDate = user.planEndDate.toDate();
          endDate.setMonth(endDate.getMonth() + 6);
        } else {
          endDate = new Date();
          endDate.setMonth(endDate.getMonth() + 6);
        }
        message = 'Standardプラン（半年無料）を適用しました！';
        break;
      case 'free_1year':
        if (user.planEndDate && user.planEndDate.toDate() > new Date()) {
          endDate = user.planEndDate.toDate();
          endDate.setFullYear(endDate.getFullYear() + 1);
        } else {
          endDate = new Date();
          endDate.setFullYear(endDate.getFullYear() + 1);
        }
        message = 'Standardプラン（1年無料）を適用しました！';
        break;
      case 'discount_50':
      case 'fixed_price':
        // 永年割引は終了日なし
        message = `Standardプラン（${campaign.campaignType === 'discount_50' ? '50%OFF' : `¥${campaign.discountAmount}/月`}）を適用しました！`;
        break;
    }
    
    // ユーザーのプランを更新
    const updatedCampaignCodes = [...usedCampaignCodes, campaignCode.toUpperCase()];
    batch.update(userRef, {
      plan: 'Standard',
      planStartDate: now,
      planEndDate: endDate ? Timestamp.fromDate(endDate) : null,
      usedCampaignCode: campaignCode.toUpperCase(), // 後方互換性のため保持
      usedCampaignCodes: updatedCampaignCodes, // 配列で管理
      updatedAt: now,
    });
    
    // キャンペーンコードの使用回数を更新
    const campaignRef = doc(db, 'campaignCodes', campaign.id);
    batch.update(campaignRef, {
      currentUses: campaign.currentUses + 1,
      updatedAt: now,
    });
    
    // 利用履歴を記録
    const historyRef = collection(db, 'users', userId, 'codeUsageHistory');
    const historyData: Record<string, any> = {
      userId: user.uid,
      userName: user.displayName,
      userEmail: user.email,
      codeType: 'campaign',
      code: campaignCode.toUpperCase(),
      appliedAt: now,
      planBefore: user.plan,
      planAfter: 'Standard',
      endDateAfter: endDate ? Timestamp.fromDate(endDate) : null,
    };
    // Only add endDateBefore if it exists
    if (user.planEndDate) {
      historyData.endDateBefore = user.planEndDate;
    }
    batch.set(doc(historyRef), historyData);
    
    await batch.commit();
    
    console.log('✅ Campaign code applied successfully:', {
      userId,
      campaignCode: campaignCode.toUpperCase(),
      planBefore: user.plan,
      planAfter: 'Standard',
      endDate: endDate ? endDate.toISOString() : null,
      historyCreated: true,
    });
    
    return { success: true, message };
  } catch (error) {
    console.error('❌ Error applying campaign code:', error);
    if (error instanceof Error) {
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
    }
    return { success: false, message: 'キャンペーンコードの適用に失敗しました' };
  }
};
